import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';

import usePlanOwner from '@/hooks/usePlanOwner';

import { createCurrentLanguageValue } from '@/utils/language';

import AddPlanWithOperators from '@/components/popups/AddPlanWithOperators';

import Constants, { routes } from '@/constants';

import { queryClient } from '@/services';

import {
  createPlan,
  getActivePlanAssignmentsQueryKey,
  getManagedPlansQueryKey,
  listAssignableChecklistsQueryKey,
  listAssignedChecklistsQueryKey,
  PlanCreateRequest,
} from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';

export interface AddPlanData {
  name: string;
  operatorId?: string;
  locationNumber?: string;
}

interface AddPlanProps {
  setShowCustomPlan: (show: boolean) => void;
  setShowInvalidNameError: (show: boolean) => void;
  showCustomPlan: boolean;
}

const AddPlan: React.FC<AddPlanProps> = ({
  setShowCustomPlan,
  setShowInvalidNameError,
  showCustomPlan,
}) => {
  const navigate = useNavigate();
  const track = useAmplitudeTrack();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const { resolvePlanOwner: resolveOwner } = usePlanOwner();

  const addTrainingPlan = async (plan: PlanCreateRequest) =>
    await createPlan({ body: plan });

  const isValidPlanName = useIsValidPlanName({
    onInvalidName: () => setShowInvalidNameError(true),
  });

  const onAddPlan = ({ name, locationNumber, operatorId }: AddPlanData) => {
    setShowCustomPlan(false);

    const trimmedName = name.trim();
    const ownerPayload = resolveOwner({
      locationNumber,
      operatorId,
    });
    const ownerId = ownerPayload.locationNumber ?? ownerPayload.ownerId;

    if (!ownerId) {
      datadogRum.addError(
        new Error('Missing owner id when adding training plan'),
      );
      return;
    }
    if (!isValidPlanName(trimmedName, ownerId)) {
      return;
    }

    addTrainingPlan({
      name: createCurrentLanguageValue(trimmedName, userLanguage),
      category: Constants.PLAN_CATEGORIES.GENERAL,
      ...ownerPayload,
    })
      .then(async ({ data }) => {
        if (!data) {
          throw new Error('No data returned from createPlan');
        }

        const { id } = data;

        // Use refetchQueries to actually wait for refetches to complete
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: listAssignableChecklistsQueryKey(),
          }),
          queryClient.refetchQueries({
            queryKey: getManagedPlansQueryKey({ query: { status: 'ACTIVE' } }),
          }),
          // In case you assign a Plan to yourself, we do a refetch.
          queryClient.refetchQueries({
            queryKey: getActivePlanAssignmentsQueryKey(),
          }),
          queryClient.refetchQueries({
            queryKey: listAssignedChecklistsQueryKey(),
          }),
        ]);

        // fire amplitude event
        if (track !== null) {
          track('training_plan_created', {
            training_plan_id: id,
            owner: ownerId,
          });
        }
        navigate(routes.plans.managePlansEdit({ planId: id }));
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  return (
    <AddPlanWithOperators
      open={showCustomPlan}
      setOpen={setShowCustomPlan}
      submitHandler={onAddPlan}
    />
  );
};

export default AddPlan;
